import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { Pagination } from 'app/shared/pagination';
import { UIGridBaseComponent } from 'app/ui/ui-grid-base.component';
import { AlertEditModalComponent } from '../alert-edit-modal/alert-edit-modal.component';
import { ModalAlertPopupComponent } from 'app/modal/modal-alert-popup/modal-alert-popup.component';
import { NOTIFICATION_MODULES } from 'app/settings/settings-notification-fixture.service';

// Alerts raised before notification triggers existed carry only an alert_type. A Snagg user
// joining is filed under Workhub, the module Snagg belongs to.
const LEGACY_TYPE_MODULE = {
	quote_accept: 'quoting',
	quote_comment: 'quoting',
	quote_review_approve: 'quoting',
	quote_review_reject: 'quoting',
	catalogue_approve: 'catalogue',
	catalogue_reject: 'catalogue',
	catalogue_request: 'catalogue',
	new_manual_task: 'workhub',
	new_task_note: 'workhub',
	new_snagg_user: 'workhub'
};

const MODULE_ICON = {
	quoting: 'wq wq-handshake',
	crm: 'wq wq-trending-up',
	projects: 'wq wq-project',
	workhub: 'wq wq-work-order',
	procurement: 'wq wq-cart',
	inventory: 'wq wq-product',
	catalogue: 'wq wq-catalogue',
	accounting: 'wq wq-money',
	others: 'wq wq-alert'
};

@Component({
	selector: 'app-alert-inbox',
	templateUrl: './alert-inbox.component.html',
	styleUrls: ['./alert-inbox.component.scss']
})
export class AlertInboxComponent extends UIGridBaseComponent implements OnInit {

	@ViewChild(AlertEditModalComponent) alertEditModal: AlertEditModalComponent;
	@ViewChild(ModalAlertPopupComponent) alertPopupModal: ModalAlertPopupComponent;

	@Input() embedded = false;

	list;
	pagination = new Pagination();

	topTab: 'attention' | 'announcements' = 'attention';
	moduleTab = 'all';
	filter: 'all' | 'unread' | 'unresolved' = 'all';

	selected = new Set<number>();
	_selectMode = false;

	moduleTabs: { key: string; label: string }[] = [];
	// The modules that can raise an alert for this organisation, from alert/modules.
	availableModules: string[] = null;

	constructor(
		public app: AppService,
		private api: ApiService,
		private router: Router,
		private el: ElementRef
	) {
		super();
	}

	ngOnInit() {
		// Fetched once: which modules an organisation has does not change while the inbox is open.
		// If it fails, the tabs fall back to the modules the alerts themselves belong to.
		this.api.alert.modules(modules => {
			this.availableModules = modules || [];
			this.buildModuleTabsView();
		}, () => {
			this.availableModules = [];
			this.buildModuleTabsView();
		});
		this.refresh();
	}

	refresh(clear = true) {
		if (clear) this.list = null;
		this.api.alert.list(list => {
			this.list = list;
			this.buildModuleTabsView();
		});
	}

	isAnnouncement(item: any): boolean {
		return item.alert_type === 'wequote';
	}

	getAlertModule(item: any): string {
		const module = item.trigger_module || LEGACY_TYPE_MODULE[item.alert_type];
		return this.moduleTabs.some(t => t.key === module && t.key !== 'all') ? module : 'others';
	}

	getAlertIcon(item: any): string {
		if (this.isAnnouncement(item)) return 'wq wq-ads';
		return MODULE_ICON[this.getAlertModule(item)] || 'wq wq-alert';
	}

	getAlertModuleLabel(item: any): string {
		const module = this.getAlertModule(item);
		const tab = this.moduleTabs.find(t => t.key === module);
		return tab ? tab.label : '';
	}

	get attentionAlerts(): any[] {
		return (this.list || []).filter(item => !this.isAnnouncement(item));
	}

	get announcementAlerts(): any[] {
		return (this.list || []).filter(item => this.isAnnouncement(item));
	}

	get visibleList(): any[] {
		const base = this.topTab === 'announcements' ? this.announcementAlerts : this.attentionAlerts;
		return base.filter(item => {
			if (this.filter === 'unread' && item.dismissed) return false;
			if (this.filter === 'unresolved' && item.resolved) return false;
			if (this.topTab === 'attention' && this.moduleTab !== 'all' && this.getAlertModule(item) !== this.moduleTab) return false;
			return true;
		});
	}

	get pageList(): any[] {
		const start = this.pagination.startPosition();
		return this.visibleList.slice(start, start + this.pagination.pageSize);
	}

	get selectMode(): boolean {
		return this._selectMode;
	}

	set selectMode(value: boolean) {
		if (this._selectMode !== !!value) this.selected.clear();
		this._selectMode = !!value;
	}

	get moduleCounts(): { [key: string]: number } {
		const counts: { [key: string]: number } = {};
		this.moduleTabs.forEach(tab => counts[tab.key] = 0);
		this.attentionAlerts.forEach(item => {
			if (item.dismissed) return;
			counts.all++;
			const module = this.getAlertModule(item);
			if (counts[module] !== undefined) counts[module]++;
		});
		return counts;
	}

	get announcementCount(): number {
		return this.announcementAlerts.filter(item => !item.dismissed).length;
	}

	get selectableList(): any[] {
		if (this.topTab !== 'attention') return [];
		return this.visibleList.filter(item => !item.resolved);
	}

	get pageSelectableList(): any[] {
		if (this.topTab !== 'attention') return [];
		return this.pageList.filter(item => !item.resolved);
	}

	get allSelected(): boolean {
		const list = this.pageSelectableList;
		return list.length > 0 && list.every(item => this.selected.has(item.id));
	}

	isSelected(item: any): boolean {
		return this.selected.has(item.id);
	}

	toggleSelect(item: any) {
		if (this.selected.has(item.id)) this.selected.delete(item.id);
		else this.selected.add(item.id);
	}

	toggleSelectAll() {
		const list = this.pageSelectableList;
		const allOn = list.length > 0 && list.every(item => this.selected.has(item.id));
		if (allOn) list.forEach(item => this.selected.delete(item.id));
		else list.forEach(item => this.selected.add(item.id));
	}

	clearSelection() {
		this.selected.clear();
	}

	onRowClick(item: any) {
		if (this.selectMode) {
			if (!item.resolved) this.toggleSelect(item);
			return;
		}
		this.openAlert(item);
	}

	selectTopTab(tab: 'attention' | 'announcements') {
		this.topTab = tab;
		this.pagination.page = 1;
		this.scrollListToTop();
	}

	selectModuleTab(key: string) {
		this.moduleTab = key;
		this.pagination.page = 1;
		this.scrollListToTop();
	}

	private scrollListToTop() {
		setTimeout(() => {
			const host = this.el.nativeElement as HTMLElement;
			const scroller = host.querySelector('.scrollable, .card-body.flex-weight-1') as HTMLElement;
			if (scroller) scroller.scrollTop = 0;
		});
	}

	moduleTabsView: { value: string, label: string }[] = [];

	private buildModuleTabsView() {
		// Every module the organisation can be alerted about gets a tab, alerts or not, plus any
		// module an existing alert belongs to. Others is always there for alerts in no module.
		const fromAlerts = this.attentionAlerts.map(item => item.trigger_module || LEGACY_TYPE_MODULE[item.alert_type]);
		const present = new Set([...(this.availableModules || []), ...fromAlerts]);
		this.moduleTabs = [
			{ key: 'all', label: 'All' },
			...NOTIFICATION_MODULES.filter(m => present.has(m.key)),
			{ key: 'others', label: 'Others' }
		];
		if (!this.moduleTabs.some(tab => tab.key === this.moduleTab)) this.moduleTab = 'all';

		const counts = this.moduleCounts;
		this.moduleTabsView = this.moduleTabs.map(tab => ({
			value: tab.key,
			label: tab.label + (counts[tab.key] ? ` <span class="badge badge-primary ml-1">${counts[tab.key]}</span>` : '')
		}));
	}

	dismissAll() {
		this.app.dialog.pop('Mark All as Read', 'Are you sure you want to mark all alerts as read?', ['Cancel', '*Mark all as read'], button => {
			if (button !== 1) return;
			this.api.alert.dismissAll(() => {
				this.api.refreshAlerts();
				this.refresh(false);
			});
		});
	}

	dismissItem(item: any, event: Event) {
		event.stopPropagation();
		this.api.alert.dismiss(item.id, () => {
			this.api.refreshAlerts();
			this.refresh(false);
		});
	}

	resolveItem(item: any, event: Event) {
		event.stopPropagation();
		this.api.alert.resolve(item.id, () => {
			this.selected.delete(item.id);
			this.api.refreshAlerts();
			this.refresh(false);
		});
	}

	unresolveItem(item: any, event: Event) {
		event.stopPropagation();
		this.api.alert.unresolve(item.id, () => {
			this.api.refreshAlerts();
			this.refresh(false);
		});
	}

	resolveSelected() {
		const ids = Array.from(this.selected);
		if (!ids.length) return;
		this.app.dialog.pop('Mark as Resolved', `Are you sure you want to mark ${ids.length} alert${ids.length > 1 ? 's' : ''} as resolved? This will also mark ${ids.length > 1 ? 'them' : 'it'} as read.`, ['Cancel', '*Mark as resolved'], button => {
			if (button !== 1) return;
			this.api.alert.resolveMultiple(ids, () => {
				this.selectMode = false;
				this.api.refreshAlerts();
				this.refresh(false);
			});
		});
	}
	
	trackByAlertId(_index: number, item: any) {
		return item.id;
	}


	openAlert(item: any) {
		if (item.trigger_id && item.action_url) {
			window.open(item.action_url, '_blank');
			this.api.alert.dismiss(item.id, () => {
				this.api.refreshAlerts();
				this.refresh(false);
			});
			return;
		}

		this.api.alert.details(item.id, data => {
			this.api.alert.dismiss(item.id, () => {
				this.api.refreshAlerts();
				this.refresh(false);
			});

			switch (data.alert_type) {
				case 'wequote':
					this.alertPopupModal.open([data]);
					break;

				case 'new_snagg_user':
					this.app.dialog.pop('New Snagg User', data.title, ['Close', '*View Snagg Users'], button => {
						if (button === 1) {
							this.router.navigate(['/', this.app.org, 'settings', 'integration']);
						}
					});
					break;

				case 'new_manual_task':
					this.app.dialog.pop('New Manual Task', data.title, ['Close', '*View Job'], button => {
						if (button === 1) {
							this.router.navigate(['/', this.app.org, 'job', data.work_order_id]);
						}
					});
					break;

				case 'new_task_note':
					this.app.dialog.pop('New Task Note', data.title, ['Close'], _button => { });
					break;

				case 'catalogue_approve':
					this.app.dialog.pop('Catalogue Request Approved', data.title, ['Close', '*View Linked Catalogues'], button => {
						if (button === 1) {
							this.router.navigate(['/', this.app.org, 'catalogue', 'linked']);
						}
					});
					break;

				case 'catalogue_reject':
					this.app.dialog.pop('Catalogue Request Rejected', data.title, ['Close', '*View Linked Catalogues'], button => {
						if (button === 1) {
							this.router.navigate(['/', this.app.org, 'catalogue', 'linked']);
						}
					});
					break;

				case 'catalogue_request':
					this.app.dialog.pop('Catalogue Request', data.title, ['Close', '*View Catalogue Requests'], button => {
						if (button === 1) {
							this.router.navigate(['/', this.app.org, 'catalogue', 'subscriptions']);
						}
					});
					break;

				case 'quote_accept':
					this.app.dialog.pop('Quote Accepted', data.title, ['Close', '*View Quote Summary'], button => {
						if (button === 1) {
							this.router.navigate(['/', this.app.org, 'quote', data.quote_id]);
						}
					});
					break;

				case 'quote_comment':
					this.app.dialog.pop('Customer Comment on Quote', data.title, ['Close', '*View Quote Summary'], button => {
						if (button === 1) {
							this.router.navigate(['/', this.app.org, 'quote', data.quote_id]);
						}
					});
					break;
			}
		});
	}

}
